/**
 * Encodes [lat, lon] pairs as a Google polyline string (precision 1e5) — mirrors the
 * backend's RideGeometry.EncodePolyline. Used to build the overview thumbnail for a
 * locally-recorded ride before it uploads (the server recomputes it on save). Points are
 * already capture-downsampled, so no extra simplification here.
 */
export function encodePolyline(points: [number, number][]): string {
  let lastLat = 0;
  let lastLon = 0;
  let result = '';

  for (const [lat, lon] of points) {
    const iLat = Math.round(lat * 1e5);
    const iLon = Math.round(lon * 1e5);
    result += encodeValue(iLat - lastLat) + encodeValue(iLon - lastLon);
    lastLat = iLat;
    lastLon = iLon;
  }

  return result;

  function encodeValue(value: number): string {
    let v = value < 0 ? ~(value << 1) : value << 1;
    let chunk = '';
    while (v >= 0x20) {
      chunk += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
      v >>= 5;
    }
    chunk += String.fromCharCode(v + 63);
    return chunk;
  }
}

/**
 * Decodes a Google encoded-polyline string (precision 1e5) into [lat, lon] pairs — the
 * inverse of the backend's RideGeometry.EncodePolyline. Used to draw ride overview
 * thumbnails and fit the map without fetching the full track.
 */
export function decodePolyline(encoded: string): [number, number][] {
  const points: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lon = 0;

  while (index < encoded.length) {
    lat += decodeValue();
    lon += decodeValue();
    points.push([lat / 1e5, lon / 1e5]);
  }

  return points;

  // Reads one zig-zag-encoded varint starting at `index`, advancing it.
  function decodeValue(): number {
    let result = 0;
    let shift = 0;
    let b: number;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  }
}
